import React, { useState, useEffect } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useNavigate, useLocation } from "react-router-dom";
import { Modal } from "react-bootstrap";
import FormHandler from "../../Common/Form/FormHandler/FormHandler";
import "./UserTaskManagement.css";
import { useKeycloak } from "@react-keycloak/web";
import { toast, ToastContainer } from "react-toastify";
import {
  TaskMgntFormConfig,
  usertaskmgntschema,
} from "../../../../JsonData/CreateTaskMgntConfig";
import { postDataWithBody, getData } from "../../../../Services/Services";
import {
  getAllRole,
  createNewTask,
  getUsersFromAppByRole,
  getThemeURL,
} from "../../../../Services/EndPoints";
import { yupResolver } from "@hookform/resolvers/yup";

const CreateUserTaskForm = () => {
  const { keycloak } = useKeycloak();

  // Component state variables
  const [activeTab, setActiveTab] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [taskMgntFormConfig, setTaskMgntFormConfig] =
    useState(TaskMgntFormConfig);
  const [showCancelModal, setShowCancelModal] = useState(false);

  // Navigation and location hooks
  const navigate = useNavigate();
  const { state } = useLocation();
  const location = useLocation();
  const { isTextFieldDisable } = location.state || {};
  let data = state?.data;

  // React Hook Form setup
  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    reset,
    control,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(usertaskmgntschema[activeTab]),
    mode: "all",
  });

  // Watch form field changes
  const assign_type = useWatch({
    name: "assign_type",
    control,
  });

  const task_type = useWatch({
    name: "task_type",
    control,
  });

  /**
   * Initial component setup
   * - Disables user list dropdown by default
   * - Loads user profile and roles
   * - Sets up view mode if data is provided
   */
  useEffect(() => {
    // Default state - disable user list dropdown
    const userListContainer = document.getElementById("container_user_list");
    if (userListContainer) {
      userListContainer.classList.add("disabled_dropdown");
    }

    // Load initial data
    getUserProfile();
    fetchAllRoles();

    // Handle view mode setup
    if (data) {
      // Hide submit button in view mode
      const submitBtn = document.getElementById("submitBtn");
      if (submitBtn) {
        submitBtn.style.display = "none";
      }

      // Populate form with existing data (excluding role_list and user_list)
      const formData = { ...data };
      delete formData.role_list;
      delete formData.user_list;
      reset(formData);

      // Configure dropdowns for view mode
      if (isTextFieldDisable) {
        toggleDropdownsBasedOnAssignType(data.assign_type);
      }
    }
  }, []);

  /**
   * Handle assignment type changes
   * Toggles between user-based and role-based assignment UI
   */
  /**
   * Handle assignment type changes
   * Toggles between user-based and role-based assignment UI
   */
  useEffect(() => {
    if (assign_type !== undefined) {
      toggleDropdownsBasedOnAssignType(assign_type);
    }
  }, [assign_type]);

  /**
   * Handle task type changes
   * Manages assign_task dropdown visibility and values
   */
  useEffect(() => {
    if (task_type !== undefined) {
      const assignTaskContainer = document.getElementById("assign_task");

      if (task_type == 1) {
        if (assignTaskContainer) {
          assignTaskContainer.classList.add("disabled_dropdown");
        }

        // Only change assign_task if not in view mode and based on assign_type
        if (!isTextFieldDisable) {
          // If assign_type is 1, set assign_task to empty string
          if (assign_type == 1) {
            setValue("assign_task", "", { shouldValidate: true });
          } else {
            setValue("assign_task", "1", { shouldValidate: true });
          }
        }
      } else if (assign_type == 0) {
        if (assignTaskContainer) {
          assignTaskContainer.classList.remove("disabled_dropdown");
        }

        // Don't automatically set assign_task in view mode
        if (!isTextFieldDisable && !getValues("assign_task")) {
          setValue("assign_task", "1", { shouldValidate: true });
        }
      }
    }
  }, [task_type, assign_type]);

  /**
   * Toggle dropdown visibility based on assignment type
   * @param {number} type - Assignment type (0: role-based, 1: user-based)
   * @param {string} preserveAssignTask - Value to preserve for assign_task field
   */
  const toggleDropdownsBasedOnAssignType = (
    type,
    preserveAssignTask = null
  ) => {
    const userListContainer = document.getElementById("container_user_list");
    const roleListContainer = document.getElementById("container_role_list");
    const assignTaskContainer = document.getElementById("assign_task");

    if (type == 1) {
      // User-based assignment
      if (userListContainer)
        userListContainer.classList.remove("disabled_dropdown");
      if (roleListContainer)
        roleListContainer.classList.add("disabled_dropdown");
      if (assignTaskContainer)
        assignTaskContainer.classList.add("disabled_dropdown");

      setValue("role_list", [], { shouldValidate: true });

      // For user-based assignment (type 1), always set assign_task to empty string
      // regardless of task_type or view mode
      setValue("assign_task", "", { shouldValidate: true });

      // Only refresh user list if not in view mode
      if (!isTextFieldDisable) {
        getUserProfile();
      }
    } else if (type == 0) {
      // Role-based assignment
      if (userListContainer)
        userListContainer.classList.add("disabled_dropdown");
      if (roleListContainer)
        roleListContainer.classList.remove("disabled_dropdown");
      if (assignTaskContainer)
        assignTaskContainer.classList.remove("disabled_dropdown");

      setValue("user_list", [], { shouldValidate: true });

      // Only override assign_task if not in view mode or preserveAssignTask not provided
      if (!isTextFieldDisable && !preserveAssignTask) {
        setValue("assign_task", "1", { shouldValidate: true });
      } else if (preserveAssignTask) {
        setValue("assign_task", preserveAssignTask, { shouldValidate: true });
      }
    }
  };

  /**
   * Fetch user profile and initialize user list
   */
  const getUserProfile = async () => {
    try {
      const userProfile = await getData(getThemeURL);
      if (userProfile && userProfile.length > 0) {
        getUserList(userProfile[0]);
      }
    } catch (error) {
      console.error("Error fetching user profile:", error);
      toast.error("Failed to fetch user profile", {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 2000,
      });
    }
  };

  /**
   * Create API payload based on user profile
   * @param {Object} profile - User profile object
   * @returns {Object} Formatted payload for API call
   */
  const createPayload = (profile) => {
    let admin_role = "U"; // default value

    if (profile.role_name === "HO") {
      admin_role = "HO";
    } else if (profile.role_name === "SUPER ADMIN") {
      admin_role = "SA";
    }

    return {
      admin_role: admin_role,
      bank_id: profile.bank_id || null,
      branch_id: profile.branch_id || null,
    };
  };

  /**
   * Fetch and populate user list for dropdown
   * @param {Object} profile - User profile for API payload
   */
  const getUserList = async (profile) => {
    try {
      var userIdNameMap = {};
      const payload = createPayload(profile);
      var userList = await postDataWithBody(getUsersFromAppByRole, payload, {});

      // Sort users alphabetically
      userList.sort((a, b) => (a.name > b.name ? 1 : -1));

      let userArrSanitized = [];
      userList.forEach((user, index) => {
        userIdNameMap[user.id] = user.first_name + " " + user.last_name;
        userArrSanitized.push({
          label: user.first_name + " " + user.last_name,
          value: user.id,
        });
      });

      // Update form configuration with user options
      const updatedConfig = [...taskMgntFormConfig];
      updatedConfig[0].fields[5]["options"] = userArrSanitized;
      setTaskMgntFormConfig(updatedConfig);

      populateUserList(userIdNameMap);
    } catch (error) {
      console.error("Error fetching user list:", error);
      toast.error("Failed to load user list");
    } finally {
    }
  };

  /**
   * Populate user list dropdown with existing data
   * @param {Object} userIdNameMap - Mapping of user IDs to names
   */
  const populateUserList = (userIdNameMap) => {
    if (
      data &&
      data.user_list &&
      (!isTextFieldDisable || data.assign_type === 1)
    ) {
      const userIds = data.user_list.split(",");

      const userOptions = userIds
        .map((id) => ({
          label: userIdNameMap[id] || id,
          value: id,
        }))
        .filter((option) => option.label);

      setValue("user_list", userOptions, { shouldValidate: true });
    } else if (isTextFieldDisable && data?.assign_type === 0) {
      // Clear user list if in view mode and assign type is role-based
      setValue("user_list", [], { shouldValidate: true });
    }
  };

  /**
   * Populate role list dropdown with existing data
   * @param {Object} roleIdNameMap - Mapping of role IDs to names
   */
  const populateRoleList = (roleIdNameMap) => {
    if (data && data.role_list) {
      const roleIds = data.role_list.split(",");

      const roleOptions = roleIds
        .map((id) => ({
          label: roleIdNameMap[id] || id,
          value: id,
        }))
        .filter((option) => option.label);

      setValue("role_list", roleOptions, { shouldValidate: true });
    }
  };

  /**
   * Fetch all available roles for dropdown
   */
  const fetchAllRoles = async () => {
    try {
      let rolearr = await getData(getAllRole);

      // Sort roles alphabetically
      rolearr.sort((a, b) => (a.name > b.name ? 1 : -1));

      let roleArrSanitized = [];
      var roleIdNameMap = {};
      rolearr.forEach((role, index) => {
        roleIdNameMap[role.id] = role.name;
        roleArrSanitized.push({
          label: role.name,
          value: role.id,
        });
      });

      // Update form configuration with role options
      const updatedConfig = [...taskMgntFormConfig];
      updatedConfig[0].fields[4]["options"] = roleArrSanitized;
      setTaskMgntFormConfig(updatedConfig);

      populateRoleList(roleIdNameMap);
    } catch (error) {
      console.error("Error fetching roles:", error);
      toast.error("Failed to load roles");
    } finally {
    }
  };

  /**
   * Generate form component using FormHandler
   * @param {Array} fields - Form field configuration
   * @param {Function} register - React Hook Form register function
   * @param {Object} errors - Form validation errors
   * @param {Object} control - React Hook Form control object
   * @returns {JSX.Element} Rendered form component
   */
  const GenerateForm = (fields, register, errors, control) => {
    return (
      <div>
        <FormHandler
          register={register}
          errors={errors}
          fields={fields}
          control={control}
          columns={2}
          getValues={getValues}
          isTextFieldDisable={isTextFieldDisable}
        />
      </div>
    );
  };

  /**
   * Generate tab components with form data
   * @param {Array} tabWiseData - Tab configuration data
   * @returns {Array} Updated tab data with components
   */
  const generateTabsData = (tabWiseData) => {
    tabWiseData?.forEach((element) => {
      return (element.component = GenerateForm(
        element?.fields,
        register,
        errors,
        control
      ));
    });
    return tabWiseData;
  };

  /**
   * Handle form submission
   * @param {Object} data - Form data
   * @param {Event} e - Form submit event
   */
  const submitHandler = async (data, e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const formData = new FormData();

    // Add user email from Keycloak token
    formData.append("email", keycloak.idTokenParsed.email);

    // Process user_list as comma-separated string
    if (data.user_list && data.user_list.length > 0) {
      const userList = data.user_list.map((user) => user.value).join(",");
      formData.append("user_list", userList);
    } else {
      formData.append("user_list", "");
    }

    // Process role_list as comma-separated string
    if (data.role_list && data.role_list.length > 0) {
      const roleList = data.role_list.map((role) => role.value).join(",");
      formData.append("role_list", roleList);
    } else {
      formData.append("role_list", "");
    }

    // Add all other form fields
    for (let key in data) {
      if (!["user_list", "role_list"].includes(key)) {
        formData.append(key, data[key]);
      }
    }

    try {
      const headers = { "Content-Type": "multipart/form-data" };
      const res = await postDataWithBody(createNewTask, formData, headers);

      if (res.status === "success") {
        toast.success(res.msg, {
          position: toast.POSITION.TOP_CENTER,
          autoClose: 2000,
        });
        navigate("/my-tasks/view-usertask");
      } else {
        toast.error(res.msg, {
          position: toast.POSITION.TOP_CENTER,
          autoClose: 2000,
        });
        setIsSubmitting(false);
      }
    } catch (error) {
      console.error("Form submission error:", error);
      toast.error("An error occurred while submitting the form", {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 2000,
      });
      setIsSubmitting(false);
    } finally {
    }
  };

  /**
   * Handle cancel button click - show confirmation modal
   */
  const handleCancelClick = () => {
    setShowCancelModal(true);
  };

  /**
   * Reset form and close modal when user confirms cancel
   */
  const handleConfirmCancel = () => {
    // Reset HTML form element
    const formElement = document.getElementsByName("CreateFacilityRegForm")[0];
    if (formElement) {
      formElement.reset();
    }

    // Reset React Hook Form state with default values
    reset({
      task_name: "",
      description: "",
      due_date: "",
      assign_type: "0",
      task_type: "0",
      assign_task: "1",
      priority: "0",
      status: "0",
      user_list: [],
      role_list: [],
    });

    // Manually clear text input fields
    const fieldsToClear = ["task_name", "description", "due_date"];
    fieldsToClear.forEach((fieldName) => {
      setValue(fieldName, "");

      // Clear DOM input elements
      const fieldInput =
        document.querySelector(`input[name="${fieldName}"]`) ||
        document.querySelector(`textarea[name="${fieldName}"]`);
      if (fieldInput) {
        fieldInput.value = "";
      }
    });

    // Reset dropdown values
    setValue("assign_type", "0");
    setValue("task_type", "0");
    setValue("assign_task", "1");
    setValue("priority", "0");
    setValue("status", "0");
    setValue("user_list", []);
    setValue("role_list", []);

    // Reset UI to default state (role-based assignment)
    toggleDropdownsBasedOnAssignType(0);

    const userListContainer = document.getElementById("container_user_list");
    const roleListContainer = document.getElementById("container_role_list");
    if (userListContainer) userListContainer.classList.add("disabled_dropdown");
    if (roleListContainer)
      roleListContainer.classList.remove("disabled_dropdown");

    setShowCancelModal(false);
  };

  // Generate tab data with components
  const tabsData = generateTabsData(taskMgntFormConfig);

  return (
    <div className="mt-4 facility-container">
      <div className="form-body">
        <div className="registration__form--body">
          <form
            onSubmit={handleSubmit(submitHandler)}
            name="CreateFacilityRegForm"
          >
            <div>
              {/* Render active tab content */}
              {tabsData?.map((tab) => (
                <React.Fragment key={tab.id}>
                  {tab.id === activeTab && <div>{tab.component}</div>}
                </React.Fragment>
              ))}
            </div>

            {/* Form action buttons */}
            <div
              className="form-submit-container full-width-field"
              style={{ marginTop: "50px" }}
            >
              {tabsData.length - 1 === activeTab && (
                <>
                  <button
                    type="submit"
                    id="submitBtn"
                    className="branch-save-submit-btn btn btn-success"
                    style={{
                      backgroundColor: "#1a7a73",
                      fontFamily: "Poppins",
                      fontSize: "14px",
                      marginRight: "10px",
                    }}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Submitting..." : "Save & Submit"}
                  </button>

                  <button
                    type="button"
                    className="branch-save-submit-btn btn btn-success"
                    onClick={handleCancelClick}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </button>
                </>
              )}
            </div>
          </form>
        </div>
      </div>

      {/* Cancel Confirmation Modal */}
      <Modal
        show={showCancelModal}
        onHide={() => setShowCancelModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm</Modal.Title>
        </Modal.Header>
        <Modal.Body>Would you like to clear the input values?</Modal.Body>
        <Modal.Footer>
          <button
            className="btn"
            style={{
              backgroundColor: "lightgray",
              color: "#000",
              marginRight: "10px",
            }}
            onClick={() => setShowCancelModal(false)}
          >
            No
          </button>
          <button
            className="btn"
            style={{
              backgroundColor: "green",
              color: "#fff",
            }}
            onClick={handleConfirmCancel}
          >
            Yes
          </button>
        </Modal.Footer>
      </Modal>

      {/* Toast notifications */}
      <ToastContainer />
    </div>
  );
};

export default CreateUserTaskForm;
